/**
 * The three requests the session screen makes, and the one rule that governs
 * all of them: **"not ready yet" is not an error.**
 *
 * A healthy session spends its first few minutes with no transcript, no
 * metrics and no activity document, because the pipeline has not finished. The
 * upstream pointer view is intentionally null in that window. So:
 *
 *   - a 409 `transcript_not_ready` / `activity_not_ready` is a STATE, and these
 *     hooks return it as one rather than letting it surface as a red error;
 *   - the detail query is PUSHED to for lifecycle/pipeline changes — the BFF's
 *     `GET /api/events` invalidates it when a webhook delivery is applied
 *     (`lib/live.ts`) — and polls on a slow backstop behind that. Browser QoE
 *     has no webhook event, so only that collecting window follows the
 *     uploader's 30-second cadence. Retained video uses `composite_ready` and
 *     keeps only a slow missed-event backstop. Everything stops once settled.
 *
 * The polling stop condition is the subtle part. `ended` is NOT terminal — the
 * call is over but the transcript is still being made — so a poller that stops
 * on "the call finished" never sees the transcript appear. See
 * `isTerminalState` in `@era/shared`.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import {
  type ActivityDocument,
  type SessionDetailView,
  type TranscriptDocument,
} from '@era/shared'
import { ApiError, api } from '@/lib/api'
import { useLiveUpdates } from '@/lib/live'
import { keys } from '@/lib/query'
import { isSessionSettled, needsMediaQualityPolling } from './settled'
import { deriveVideoState, isVideoSettled, type SessionVideoStatus } from './video'

/**
 * How often an unfinished session is re-asked about when nothing is pushing.
 *
 * Unchanged: this is what the screen degrades TO, and it is the cadence that
 * worked before `/api/events` existed.
 */
const POLL_MS = 15_000

/**
 * How often it is re-asked about when the stream is up.
 *
 * The push does the work now — `transcript_ready` reaches the BFF about a
 * second after the pipeline finishes and this query is invalidated ~15 ms
 * later — so what is left for the timer is the cases a push cannot reach.
 * FINDINGS #33 asked for the poll to be deleted outright once the stream
 * existed; it is not, and `lib/live.ts` sets out the three reasons. The short
 * one: a buffered SSE connection reports itself healthy and delivers nothing,
 * and a reference client that freezes in that state teaches everyone who
 * copies it to freeze too.
 *
 * Ninety seconds against `transcript_ready`'s several-minute pipeline means a
 * totally broken stream still finishes a session roughly when it used to,
 * one order of magnitude fewer requests along the way.
 */
const BACKSTOP_MS = 90_000
/** Browser QoE has no SSE event; align an open detail view with its 30s uploader. */
const MEDIA_QUALITY_POLL_MS = 30_000

export function useSessionDetail(sessionId: string): UseQueryResult<SessionDetailView, ApiError> {
  const live = useLiveUpdates()

  return useQuery<SessionDetailView, ApiError>({
    queryKey: keys.session(sessionId),
    queryFn: () => api<SessionDetailView>(`/api/sessions/${encodeURIComponent(sessionId)}`),
    // `query.state.data` rather than a captured variable: the callback is
    // evaluated after every fetch, so this reads the freshest answer and stops
    // by itself the first time the session settles. The stop condition is
    // unchanged by the stream — a settled session has nothing left to push
    // either.
    refetchInterval: (query) => {
      const view = query.state.data
      if (isSessionSettled(view)) return false
      if (needsMediaQualityPolling(view)) return MEDIA_QUALITY_POLL_MS
      return live ? BACKSTOP_MS : POLL_MS
    },
    staleTime: 5_000,
  })
}

/* -------------------------------------------------------------------------- */
/* the two documents                                                           */
/* -------------------------------------------------------------------------- */

/**
 * A document that is legitimately not there yet, distinguished from one that
 * failed.
 *
 * `pending` is the answer to a 409 and carries the reason the server gave, so
 * the screen can say "still transcribing" or "this room was created with
 * recording off" instead of the same shrug for both.
 */
export type DocumentState<T> =
  | { status: 'loading' }
  | { status: 'ready'; document: T }
  | { status: 'pending'; reason: string }
  | { status: 'error'; error: ApiError }

export function useTranscript(
  sessionId: string,
  enabled: boolean,
): DocumentState<TranscriptDocument> {
  const query = useQuery<TranscriptDocument, ApiError>({
    queryKey: keys.transcript(sessionId),
    queryFn: () =>
      api<TranscriptDocument>(`/api/sessions/${encodeURIComponent(sessionId)}/transcript`),
    enabled,
    // A transcript is immutable once generated. Re-fetching it on every window
    // focus would re-download the whole document to learn nothing.
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 10 * 60_000,
    retry: (count, error) => error.status >= 500 && count < 2,
  })
  return toDocumentState(query, enabled)
}

export function useActivity(sessionId: string, enabled: boolean): DocumentState<ActivityDocument> {
  const query = useQuery<ActivityDocument, ApiError>({
    queryKey: [...keys.session(sessionId), 'activity'] as const,
    queryFn: () => api<ActivityDocument>(`/api/sessions/${encodeURIComponent(sessionId)}/activity`),
    enabled,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 10 * 60_000,
    retry: (count, error) => error.status >= 500 && count < 2,
  })
  return toDocumentState(query, enabled)
}

/* -------------------------------------------------------------------------- */
/* retained video                                                              */
/* -------------------------------------------------------------------------- */

/**
 * How often an unfinished render is re-asked about.
 *
 * TEN seconds, because polling is currently the ONLY transport. This was 90s
 * on the premise that `composite_ready` pushes completion and this interval is
 * just a missed-event backstop — but that event does not exist on the deployed
 * control plane (its webhook enum is still the original four; see the block
 * comment on `SUBSCRIBED_EVENTS` in `apps/bff/src/era/webhook-events.ts`), so
 * we never subscribe to it and no push ever arrives. At 90s a finished video
 * took up to a minute and a half to appear on screen with no indication that
 * anything was happening.
 *
 * A backstop interval and a primary interval are different numbers for the
 * same field, and which one is correct depends entirely on whether the push
 * half is real. `composite_ready` is now subscribed and invalidates this exact
 * query, so 90 seconds is missed/buffered-event recovery rather than the
 * transport that discovers an ordinary completed render.
 *
 * The query key below MUST stay in step with the one `lib/live.ts` invalidates.
 */
const VIDEO_BACKSTOP_MS = 90_000

export function useSessionVideo(
  sessionId: string,
  enabled: boolean,
): UseQueryResult<SessionVideoStatus, ApiError> {
  return useQuery<SessionVideoStatus, ApiError>({
    // Derived from the session key rather than added to `keys`, the same way
    // `useActivity` above does it.
    //
    // NOT free-floating: `lib/live.ts` invalidates this exact array when a
    // `composite_ready` frame arrives, with `exact: true`. Change the shape
    // here and the push silently stops reaching this query — it would not
    // error, the video would just go back to appearing on the next poll.
    queryKey: [...keys.session(sessionId), 'video'] as const,
    queryFn: () =>
      api<SessionVideoStatus>(`/api/sessions/${encodeURIComponent(sessionId)}/video/status`),
    enabled,
    // Read from `query.state.data` so the stop condition sees the freshest
    // answer, the same shape the detail poll uses. `deriveVideoState` is pure
    // and cheap, and running it here keeps ONE definition of "settled" for the
    // poll and for the panel — a second one would eventually disagree, and the
    // failure mode is a screen that has the file and keeps asking for it.
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return VIDEO_BACKSTOP_MS
      return isVideoSettled(deriveVideoState(data)) ? false : VIDEO_BACKSTOP_MS
    },
    staleTime: 5_000,
    retry: (count, error) => error.status >= 500 && count < 2,
  })
}

function toDocumentState<T>(query: UseQueryResult<T, ApiError>, enabled: boolean): DocumentState<T> {
  if (query.data) return { status: 'ready', document: query.data }
  if (query.error) {
    // 409 is the server saying "this is a normal state of a healthy session",
    // and its detail names which state. 404 lands here too, for a session whose
    // artifacts retention has already removed.
    if (query.error.status === 409) return { status: 'pending', reason: query.error.message }
    if (query.error.status === 404) {
      return { status: 'pending', reason: 'the document is no longer stored for this session' }
    }
    return { status: 'error', error: query.error }
  }
  if (!enabled) return { status: 'pending', reason: 'waiting for the session to be readable' }
  return { status: 'loading' }
}
